/**
 * 指令模板的"模板族"和"操作对象策略"。
 * 以模板 meta 里的标记（meta.template_family / meta.target_policy）为准，和后端 apps/instruction/template_policy.py 一致；
 * 只有还没补标记的旧模板才按名称兜底，模板改名不影响已有标记。
 */

export type TemplateFamily =
  | 'relabel_pack'
  | 'single_dropship'
  | 'regular_outbound'
  | 'sort_count'
  | 'nonstandard_ops';

const TEMPLATE_FAMILIES: TemplateFamily[] = ['relabel_pack', 'single_dropship', 'regular_outbound', 'sort_count', 'nonstandard_ops'];

/** allow_history_ioc：能否以"历史指令执行结果（IOC）"为操作对象；require_liu：操作对象是否必须是散货库存单元 */
const DEFAULT_POLICIES: Record<TemplateFamily, { allowHistoryIoc: boolean; requireLiu: boolean }> = {
  relabel_pack: { allowHistoryIoc: false, requireLiu: true },
  single_dropship: { allowHistoryIoc: false, requireLiu: true },
  regular_outbound: { allowHistoryIoc: true, requireLiu: false },
  sort_count: { allowHistoryIoc: false, requireLiu: false },
  nonstandard_ops: { allowHistoryIoc: false, requireLiu: false },
};

/** 提示文字里的指令名称 */
const FAMILY_LABELS: Record<TemplateFamily, string> = {
  relabel_pack: '换标+装箱指令',
  single_dropship: '一件代发指令',
  regular_outbound: '常规出库指令',
  sort_count: '库存分拣与清点指令',
  nonstandard_ops: '库存非标准操作指令',
};

export interface TemplatePolicy {
  family: TemplateFamily | '';
  allowHistoryIoc: boolean;
  requireLiu: boolean;
  /** 提示文字里的指令名称，没有模板族时为空 */
  label: string;
}

const NO_POLICY: TemplatePolicy = { family: '', allowHistoryIoc: true, requireLiu: false, label: '' };

/** 模板还没有标记时，按原来的名称规则兜底 */
function familyFromName(name: string): TemplateFamily | '' {
  if (name.includes('换标+装箱') || name.includes('换标装箱')) return 'relabel_pack';
  if (name === '一件代发(系统级标准指令)') return 'single_dropship';
  if (name.includes('常规出库')) return 'regular_outbound';
  if (name === '库存分拣与清点(系统级标准指令)') return 'sort_count';
  return '';
}

export function getTemplatePolicy(tpl: any): TemplatePolicy {
  if (!tpl) return NO_POLICY;
  const meta = (tpl.meta && typeof tpl.meta === 'object') ? tpl.meta : {};
  const marked = String(meta.engine || '') === 'nonstandard_ops' ? 'nonstandard_ops' : String(meta.template_family || '').trim();
  const family: TemplateFamily | '' = (TEMPLATE_FAMILIES as string[]).includes(marked)
    ? (marked as TemplateFamily)
    : familyFromName(String(tpl.name || '').trim());
  if (!family) return NO_POLICY;
  const policy = { ...DEFAULT_POLICIES[family] };
  const explicit = (meta.target_policy && typeof meta.target_policy === 'object') ? meta.target_policy : {};
  if (typeof explicit.allow_history_ioc === 'boolean') policy.allowHistoryIoc = explicit.allow_history_ioc;
  if (typeof explicit.require_liu === 'boolean') policy.requireLiu = explicit.require_liu;
  return { family, ...policy, label: FAMILY_LABELS[family] };
}
